
import { Link, useLocation } from "wouter";
import { Home, Plus, FileText, BarChart3, User } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Avatar, Typography, Box } from "@mui/material";

const DEMO_USER_ID = "demo-user-1";

export default function Sidebar() {
  const [location] = useLocation();
  const { data: stats } = useQuery({
    queryKey: ["/api/dashboard/stats", { userId: DEMO_USER_ID }],
  });

  const isActive = (path) => {
    if (path === "/" && (location === "/" || location === "/dashboard")) return true;
    return location.startsWith(path) && path !== "/";
  };

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width: 240,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: { width: 240, boxSizing: 'border-box', backgroundColor: '#fff' }
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ width: 32, height: 32, bgcolor: 'primary.main', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FileText size={16} color="#fff" />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>Form Builder</Typography>
      </Box>

      {/* Navigation Menu */}
      <List>
        <Link href="/formDashboard">
          <ListItem disablePadding>
            <ListItemButton selected={isActive("/formDashboard") || isActive("/")}>
              <ListItemIcon><Home size={16} /></ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItemButton>
          </ListItem>
        </Link>

        <Link href="/forms/new">
          <ListItem disablePadding>
            <ListItemButton selected={isActive("/forms/new")}>
              <ListItemIcon><Plus size={16} /></ListItemIcon>
              <ListItemText primary="Create Form" />
            </ListItemButton>
          </ListItem>
        </Link>

        <Link href="/forms">
          <ListItem disablePadding>
            <ListItemButton selected={isActive("/forms") && !location.includes("/forms/new")}>
              <ListItemIcon><FileText size={16} /></ListItemIcon>
              <ListItemText primary="My Forms" />
            </ListItemButton>
          </ListItem>
        </Link>

        <ListItem disablePadding>
          <ListItemButton>
            <ListItemIcon><BarChart3 size={16} /></ListItemIcon>
            <ListItemText primary="Analytics" />
          </ListItemButton>
        </ListItem>
      </List>

      {/* Recent Forms */}
      <Box sx={{ p: 2, flex: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', color: 'text.secondary' }}>
          Recent Forms
        </Typography>
        {stats?.recentForms && stats.recentForms.length > 0 ? (
          stats.recentForms.slice(0, 3).map((form) => (
            <Link key={form._id} href={`/forms/${form._id}`}>
              <ListItemButton sx={{ pl: 0 }}>
                <ListItemIcon><FileText size={16} color="#757575" /></ListItemIcon>
                <Box>
                  <Typography variant="body2" noWrap>{form.title || "Untitled Form"}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {form.updatedAt ? formatDistanceToNow(new Date(form.updatedAt)) + " ago" : "Never"}
                  </Typography>
                </Box>
              </ListItemButton>
            </Link>
          ))
        ) : (
          <Typography variant="caption" color="text.secondary">No recent forms</Typography>
        )}
      </Box>

      <Divider />

      {/* User Profile */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, borderTop: '1px solid #e0e0e0' }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.400' }}>
          <User size={16} color="#424242" />
        </Avatar>
        <Box>
          <Typography variant="body2" fontWeight="medium">Demo User</Typography>
          <Typography variant="caption" color="text.secondary">demo@example.com</Typography>
        </Box>
      </Box>
    </Drawer>
  );
}
